import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const CUSTOMERS_API = `${API_BASE}/customers`;
const RATES_API = `${API_BASE}/rates`;

// Same helpers as CustomerDetail.jsx — maps a master rate's name/unit to the
// fixed legacy slot the pricing engine (invoice.route.js, NewBooking.jsx)
// actually reads, and derives that legacy shape from the general override
// map so nothing downstream needs to change.
function legacySlotFor(rate) {
  const name = String(rate.name || "").toLowerCase().trim();
  const unit = String(rate.unit || "").toLowerCase().trim();

  if (name === "cattle") return ["livestock", "cattlePerHead"];
  if (name === "sheep") return ["livestock", "sheepPerHead"];
  if (name === "deer") return ["livestock", "deerPerHead"];
  if (name === "other livestock" || name === "other") return ["livestock", "otherPerHead"];

  if (name === "pallet") return ["freight", "pallet"];
  if (name === "container") return ["freight", "container"];
  if (name === "vehicle") return ["freight", "vehicle"];
  if (name === "loose item" || name === "loose_item") return ["freight", "looseItem"];

  if (unit === "per_kg") return ["freight", "perKg"];
  if (unit === "per_m3") return ["freight", "perM3"];

  return null;
}

function deriveLegacyRates(standardRates, overrideMap) {
  const legacy = {
    rateSource: Object.keys(overrideMap).length > 0 ? "custom" : "standard",
    livestock: { cattlePerHead: 0, sheepPerHead: 0, deerPerHead: 0, otherPerHead: 0 },
    freight: { pallet: 0, container: 0, vehicle: 0, looseItem: 0, perKg: 0, perM3: 0 },
  };

  standardRates.forEach((rate) => {
    const slot = legacySlotFor(rate);
    if (!slot) return;
    const [section, key] = slot;
    const effectivePrice = overrideMap[rate._id] != null ? overrideMap[rate._id] : Number(rate.price || 0);
    legacy[section][key] = effectivePrice;
  });

  return legacy;
}

export default function EditCustomer() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [standardRates, setStandardRates] = useState([]);
  const [rateOverrideMap, setRateOverrideMap] = useState({});
  const [hiddenRateIds, setHiddenRateIds] = useState(new Set());
  const [selectedRateIds, setSelectedRateIds] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);

  const [form, setForm] = useState({
    companyName: "",
    tradingName: "",
    customerType: "general",
    contactName: "",
    email: "",
    phone: "",
    billingEmail: "",
    moneyworksCode: "",
    notes: "",
    status: "active",
    address: {
      line1: "",
      line2: "",
      city: "",
      region: "",
      postcode: "",
      country: "New Zealand",
    },
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [customerRes, ratesRes] = await Promise.all([
        fetch(`${CUSTOMERS_API}/${id}`),
        fetch(RATES_API),
      ]);

      const customerData = await customerRes.json();
      const ratesData = await ratesRes.json();
      const ratesList = Array.isArray(ratesData) ? ratesData : (ratesData?.data || []);

      setStandardRates(ratesList);

      setForm((prev) => ({
        ...prev,
        ...customerData,
        address: {
          ...prev.address,
          ...(customerData.address || {}),
        },
      }));

      const overrideMap = {};
      (customerData?.rateOverrides || []).forEach((o) => {
        if (o.rate) overrideMap[o.rate] = o.price;
      });
      setRateOverrideMap(overrideMap);
      setHiddenRateIds(new Set((customerData?.hiddenRateIds || []).map(String)));
    } catch (error) {
      console.error("Failed to load customer:", error);
      alert("Failed to load customer");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateAddress = (key, value) => {
    setForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [key]: value,
      },
    }));
  };

  const updateRateOverride = (rateId, value) => {
    const trimmed = String(value).trim();
    setRateOverrideMap((prev) => {
      const next = { ...prev };
      if (trimmed === "") {
        delete next[rateId];
      } else {
        next[rateId] = Number(trimmed);
      }
      return next;
    });
  };

  const resetRateOverride = (rateId) => {
    setRateOverrideMap((prev) => {
      const next = { ...prev };
      delete next[rateId];
      return next;
    });
  };

  const toggleRateSelected = (rateId) => {
    setSelectedRateIds((prev) => {
      const next = new Set(prev);
      next.has(rateId) ? next.delete(rateId) : next.add(rateId);
      return next;
    });
  };

  const hideSelectedRates = () => {
    setHiddenRateIds((prev) => new Set([...prev, ...selectedRateIds]));
    setSelectedRateIds(new Set());
  };

  const unhideRate = (rateId) => {
    setHiddenRateIds((prev) => {
      const next = new Set(prev);
      next.delete(rateId);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const rateOverrides = standardRates
        .filter((rate) => rateOverrideMap[rate._id] != null)
        .map((rate) => ({
          rate: rate._id,
          name: rate.name,
          type: rate.type,
          unit: rate.unit,
          price: rateOverrideMap[rate._id],
        }));

      const response = await fetch(`${CUSTOMERS_API}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          rates: deriveLegacyRates(standardRates, rateOverrideMap),
          rateOverrides,
          hiddenRateIds: [...hiddenRateIds],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update customer");
      }

      navigate(`/dashboard/customers/${id}`);
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Loading customer...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(`/dashboard/customers/${id}`)}
              className="mb-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              ← Back
            </button>

            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Edit Customer
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Update customer details and customer-specific rates.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/customers/${id}`)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              form="edit-customer-form"
              disabled={saving}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <form id="edit-customer-form" onSubmit={handleSave}>
          <div className="grid gap-6 xl:grid-cols-[1.7fr_0.9fr]">
            <div className="space-y-6">
              <SectionCard
                title="Customer Details"
                description="Basic customer identity, type, and contact information."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Company Name">
                    <input
                      className="input"
                      value={form.companyName}
                      onChange={(e) => updateField("companyName", e.target.value)}
                      required
                    />
                  </Field>

                  <Field label="Trading Name">
                    <input
                      className="input"
                      value={form.tradingName}
                      onChange={(e) => updateField("tradingName", e.target.value)}
                    />
                  </Field>

                  <Field label="Customer Type">
                    <select
                      className="input"
                      value={form.customerType}
                      onChange={(e) => updateField("customerType", e.target.value)}
                    >
                      <option value="general">General</option>
                      <option value="livestock">Livestock</option>
                      <option value="freight">Freight</option>
                      <option value="farmer">Farmer</option>
                      <option value="business">Business</option>
                      <option value="individual">Individual</option>
                      <option value="agent">Agent</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>

                  <Field label="Contact Name">
                    <input
                      className="input"
                      value={form.contactName}
                      onChange={(e) => updateField("contactName", e.target.value)}
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      className="input"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                    />
                  </Field>

                  <Field label="Phone">
                    <input
                      className="input"
                      value={form.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                    />
                  </Field>

                  <Field label="Status">
                    <select
                      className="input"
                      value={form.status}
                      onChange={(e) => updateField("status", e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Address"
                description="Primary customer address information."
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Address Line 1">
                    <input
                      className="input"
                      value={form.address.line1}
                      onChange={(e) => updateAddress("line1", e.target.value)}
                    />
                  </Field>

                  <Field label="Address Line 2">
                    <input
                      className="input"
                      value={form.address.line2}
                      onChange={(e) => updateAddress("line2", e.target.value)}
                    />
                  </Field>

                  <Field label="City">
                    <input
                      className="input"
                      value={form.address.city}
                      onChange={(e) => updateAddress("city", e.target.value)}
                    />
                  </Field>

                  <Field label="Region">
                    <input
                      className="input"
                      value={form.address.region}
                      onChange={(e) => updateAddress("region", e.target.value)}
                    />
                  </Field>

                  <Field label="Postcode">
                    <input
                      className="input"
                      value={form.address.postcode}
                      onChange={(e) => updateAddress("postcode", e.target.value)}
                    />
                  </Field>

                  <Field label="Country">
                    <input
                      className="input"
                      value={form.address.country}
                      onChange={(e) => updateAddress("country", e.target.value)}
                    />
                  </Field>
                </div>
              </SectionCard>

              <SectionCard
                title="Customer Rates"
                description="Every master rate is listed below. Only rows you give a different price get saved as an override — everything else uses the standard rate automatically."
              >
                {standardRates.filter((r) => r.active !== false).length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No master rates set up yet — add some in Settings → Rates first.
                  </p>
                ) : (() => {
                  const activeRates = standardRates.filter((r) => r.active !== false);
                  const visibleRates = activeRates.filter((r) => !hiddenRateIds.has(r._id));
                  const hiddenRates = activeRates.filter((r) => hiddenRateIds.has(r._id));

                  return (
                    <>
                      {selectedRateIds.size > 0 && (
                        <div className="mb-3 flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5">
                          <span className="text-xs font-semibold text-slate-600">{selectedRateIds.size} selected</span>
                          <button
                            type="button"
                            onClick={hideSelectedRates}
                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          >
                            Hide Selected ({selectedRateIds.size})
                          </button>
                        </div>
                      )}

                      <div className="overflow-x-auto rounded-2xl border border-slate-200">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr className="text-left">
                              <th className="px-4 py-3 w-10">
                                <input
                                  type="checkbox"
                                  checked={visibleRates.length > 0 && visibleRates.every((r) => selectedRateIds.has(r._id))}
                                  onChange={() => {
                                    setSelectedRateIds((prev) => {
                                      const allSelected = visibleRates.every((r) => prev.has(r._id));
                                      return allSelected ? new Set() : new Set(visibleRates.map((r) => r._id));
                                    });
                                  }}
                                  className="h-4 w-4 rounded border-slate-300"
                                />
                              </th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Type</th>
                              <th className="px-4 py-3 font-semibold text-slate-700">Unit</th>
                              <th className="px-4 py-3 font-semibold text-slate-700 text-right">Standard Price</th>
                              <th className="px-4 py-3 font-semibold text-slate-700 text-right">Customer Price</th>
                              <th className="px-4 py-3 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRates.map((rate, i) => {
                              const isOverridden = rateOverrideMap[rate._id] != null;
                              return (
                                <tr key={rate._id} className={`border-t border-slate-100 ${isOverridden ? "bg-amber-50" : i % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>
                                  <td className="px-4 py-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedRateIds.has(rate._id)}
                                      onChange={() => toggleRateSelected(rate._id)}
                                      className="h-4 w-4 rounded border-slate-300"
                                    />
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-900">{rate.name}</td>
                                  <td className="px-4 py-3 text-slate-600">{toTitle(rate.type)}</td>
                                  <td className="px-4 py-3 text-slate-600">{toTitle(rate.unit)}</td>
                                  <td className="px-4 py-3 text-right text-slate-500">${Number(rate.price || 0).toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right">
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder={String(rate.price || 0)}
                                      value={rateOverrideMap[rate._id] ?? ""}
                                      onChange={(e) => updateRateOverride(rate._id, e.target.value)}
                                      className="input w-28 text-right"
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {isOverridden && (
                                      <button
                                        type="button"
                                        onClick={() => resetRateOverride(rate._id)}
                                        title="Reset to standard price"
                                        className="text-xs font-semibold text-slate-400 hover:text-red-500"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {hiddenRates.length > 0 && (
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={() => setShowHidden((v) => !v)}
                            className="text-xs font-semibold text-slate-400 hover:text-slate-700"
                          >
                            {showHidden ? "▲ Hide" : "▼ Show"} {hiddenRates.length} hidden rate{hiddenRates.length > 1 ? "s" : ""}
                          </button>
                          {showHidden && (
                            <div className="mt-3 space-y-2">
                              {hiddenRates.map((rate) => (
                                <div key={rate._id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
                                  <div className="text-sm text-slate-500">
                                    <span className="font-medium text-slate-700">{rate.name}</span> · {toTitle(rate.type)} · ${Number(rate.price || 0).toLocaleString()}
                                  </div>
                                  <button type="button" onClick={() => unhideRate(rate._id)} className="text-xs font-semibold text-slate-600 hover:text-slate-950">
                                    Unhide
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </SectionCard>

              <SectionCard
                title="Additional Information"
                description="Billing contact and optional notes."
              >
                <div className="grid grid-cols-1 gap-5">
                  <Field label="Billing Email">
                    <input
                      className="input"
                      value={form.billingEmail}
                      onChange={(e) => updateField("billingEmail", e.target.value)}
                    />
                  </Field>

                  <Field label="Moneyworks Code">
                    <input
                      className="input"
                      placeholder="Leave blank to auto-generate on first invoice"
                      maxLength={11}
                      value={form.moneyworksCode || ""}
                      onChange={(e) => updateField("moneyworksCode", e.target.value.toUpperCase())}
                    />
                  </Field>

                  <Field label="Notes">
                    <textarea
                      rows="4"
                      className="input min-h-[120px] resize-y"
                      value={form.notes}
                      onChange={(e) => updateField("notes", e.target.value)}
                    />
                  </Field>
                </div>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <AsideCard title="Customer Summary">
                <SummaryRow label="Company Name" value={form.companyName || "—"} />
                <SummaryRow label="Trading Name" value={form.tradingName || "—"} />
                <SummaryRow
                  label="Customer Type"
                  value={toTitle(form.customerType || "general")}
                />
                <SummaryRow label="Contact Name" value={form.contactName || "—"} />
                <SummaryRow label="Email" value={form.email || "—"} />
                <SummaryRow label="Phone" value={form.phone || "—"} />
              </AsideCard>

              <AsideCard title="Address Snapshot">
                <SummaryRow label="Line 1" value={form.address.line1 || "—"} />
                <SummaryRow label="City" value={form.address.city || "—"} />
                <SummaryRow label="Region" value={form.address.region || "—"} />
                <SummaryRow label="Postcode" value={form.address.postcode || "—"} />
                <SummaryRow label="Country" value={form.address.country || "—"} />
              </AsideCard>

              <AsideCard title="Rate Source">
                <SummaryRow
                  label="Mode"
                  value={Object.keys(rateOverrideMap).length > 0 ? "Custom" : "Standard"}
                />
                <SummaryRow label="Custom Rates" value={`${Object.keys(rateOverrideMap).length} of ${standardRates.filter(r => r.active !== false).length}`} />
                <SummaryRow label="Hidden Rates" value={String(hiddenRateIds.size)} />
              </AsideCard>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AsideCard({ title, children }) {
  return (
    <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      <div className="mt-4 space-y-3">{children}</div>
    </aside>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
      <span className="text-sm font-medium text-slate-500">{label}</span>
      <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}